import type {
  EventMessageComponent,
  FileAttachment,
  HandoffIntegrations,
  LiveChatSendAgentMessageResponse,
  LiveChatSendMessageResponse,
  LiveChatStatus,
  UpdateForethoughtLiveChatHelpdeskConversationIdResponse,
  WidgetComponent,
} from 'api/types';
import {
  isButtonsComponent,
  isEventMessageComponent,
  isFormComponent,
  isTextMessageComponent,
  isUserMessageComponent,
} from 'api/types';
import {
  addScreenReaderNotification,
  addWidgetComponents,
  deleteForethoughtLiveChatWidgetComponents,
  selectConversationId,
  selectFeatureFlags,
  selectIsCsatPromptableForEndLiveChat,
  selectLastWidgetComponent,
  setCsatStatesByKey,
  setDismissableNotification,
} from 'reducers/workflows';
import ApiService from 'services/ApiService';
import { createAppAsyncThunk } from 'createAppAsyncThunk';
import {
  onAgentConnected,
  resetLiveChatState,
  selectChatId,
  selectIntegrationConfig,
  selectIsInAssistantMode,
  selectLiveChatIntegration,
  selectLiveChatQuery,
  selectLiveChatStatus,
  selectLiveChatWidgetComponentExists,
  selectUserEndedChat,
  setIsAgentTyping,
  setLiveChatId,
  setLiveChatQuery,
  setLiveChatStatus,
  setUserEndedChat,
} from 'reducers/live-chat';
import { debounce } from 'utils/debounce';
import { rejectWithCustomFetchError } from 'api/customFetch';
import { openWidget } from 'actions/main';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import {
  CHAT_HANDOFF_FAILURE_MESSAGE,
  CHAT_HANDOFF_SUCCESS,
  PREFIX_TO_SKIP_RENDERING_WIDGET_COMPONENT,
  SUNCO_WAIT_TIME_NAME,
  USER_LEFT_CHAT_MSG,
} from 'components/App/consts';
import {
  clearConversation,
  updateIntentConversation,
} from 'actions/workflows-v2/workflowsV2';
import type { RootState } from 'reducers/root';
import type { AppThunk } from 'appThunk';
import { selectWidgetInitMethod } from 'reducers/config';
import {
  closeConversation,
  sendTextMessage,
} from 'components/salesforce-messaging-one-chat/messagingService';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { getErrorMessage } from 'utils/utils';
import { closeEventSource } from 'components/salesforce-messaging-one-chat/eventSourceService';
import {
  clearInMemoryData,
  clearWebStorage,
} from 'components/salesforce-messaging-one-chat/dataProvider';
import { applyZopimFunction } from 'embed/zopim';
import { applyKustomerCoreFunctionWithCallback } from 'embed/kustomerCore';
import Smooch from 'smooch';
import { clearSmoochSession } from 'persistence/utils';
import { getSmoochLocalStorage } from 'components/App/hooks/useHandleSuncoInitialization';
import { postMessage } from 'utils/postMessage';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import {
  getSmoochErrorMessage,
  processMessage,
  type SuncoMessage,
} from 'embed/smooch';
import { v4 as uuidv4 } from 'uuid';

/**
 * Filter out widget components where a duplicate component
 * exists with the same timestamp.
 */
function filterWidgetComponents({
  components,
  state,
}: {
  components: WidgetComponent[];
  state: RootState;
}): WidgetComponent[] {
  return components.filter(component => {
    // if identical text message component with timestamp is already added, don't add it again
    if (isTextMessageComponent(component) && component.timestamp) {
      return !state.workflows.widgetComponents.find(wc => {
        if (isTextMessageComponent(wc) && wc.timestamp) {
          return (
            wc.timestamp === component.timestamp &&
            wc.component_fields.message === component.component_fields.message
          );
        }

        return false;
      });
    }

    // if identical user message component with timestamp is already added, don't add it again
    if (isUserMessageComponent(component) && component.timestamp) {
      return !state.workflows.widgetComponents.find(wc => {
        if (isUserMessageComponent(wc) && wc.timestamp) {
          return (
            wc.timestamp === component.timestamp &&
            wc.component_fields.message === component.component_fields.message
          );
        }

        return false;
      });
    }

    // if identical event message component with timestamp is already added, don't add it again
    if (isEventMessageComponent(component) && component.timestamp) {
      return !state.workflows.widgetComponents.find(wc => {
        if (isEventMessageComponent(wc) && wc.timestamp) {
          return (
            wc.timestamp === component.timestamp &&
            wc.component_fields.message === component.component_fields.message
          );
        }

        return false;
      });
    }

    // if identical button component with timestamp is already added, don't add it again
    if (isButtonsComponent(component) && component.timestamp) {
      return !state.workflows.widgetComponents.find(wc => {
        if (isButtonsComponent(wc) && wc.timestamp) {
          return wc.timestamp === component.timestamp;
        }

        return false;
      });
    }

    // if identical form component with timestamp is already added, don't add it again
    if (isFormComponent(component) && component.timestamp) {
      return !state.workflows.widgetComponents.find(wc => {
        if (isFormComponent(wc) && wc.timestamp) {
          return wc.timestamp === component.timestamp;
        }
        return false;
      });
    }

    // include all other components
    return true;
  });
}

export const sendUserReply = createAppAsyncThunk(
  'liveChat/sendUserMessage',
  async (
    {
      addWidgetComponent = true,
      attachments = [],
      message,
      timestamp,
    }: {
      addWidgetComponent?: boolean;
      attachments?: FileAttachment[];
      message: string;
      timestamp?: number;
    },
    { dispatch, getState, rejectWithValue },
  ): Promise<LiveChatSendMessageResponse> => {
    const state = getState();
    const isRedactSrpEnabled =
      state.workflows.enabled_feature_flags.includes('redact_srp_words');
    const filteredWidgetComponents = filterWidgetComponents({
      components: [
        {
          component_fields: { attachments, message },
          component_type: 'user_message',
          isSent: true,
          timestamp,
        },
      ],
      state,
    });

    if (!filteredWidgetComponents.length) {
      return Promise.reject(
        new Error('No widget components left after filtering'),
      );
    }

    /**
     * Add empty text message component to not render agent
     * text messages but render all user messages. Widget
     * is built to have 1 widget component and 1 query. This
     * allows multiple user queries back to back
     */

    if (addWidgetComponent && !isRedactSrpEnabled) {
      dispatch(addWidgetComponents(filteredWidgetComponents));
    }

    try {
      const data = await ApiService.request('live-chat-send-message', {
        data: { attachments, message },
        urlValues: {
          conversationId: state.workflows.conversationId,
          helpdeskConversationId: state.liveChat.chatId,
        },
      });

      if (addWidgetComponent && isRedactSrpEnabled) {
        dispatch(
          addWidgetComponents([
            {
              component_fields: { attachments, message: data.user_reply },
              component_type: 'user_message',
              isSent: true,
            },
          ]),
        );
      }

      return data;
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

/**
 * appends message & attachments to ForethoughtLiveChat mongo document
 */
export const sendAgentReply = createAppAsyncThunk(
  'liveChat/sendAgentMessage',
  async (
    {
      attachments = [],
      message,
    }: {
      attachments?: FileAttachment[];
      message: string;
    },
    { getState, rejectWithValue },
  ): Promise<LiveChatSendAgentMessageResponse> => {
    const state = getState();

    try {
      return await ApiService.request('live-chat-send-agent-message', {
        data: { attachments, message },
        urlValues: {
          conversationId: state.workflows.conversationId,
          helpdeskConversationId: state.liveChat.chatId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

/**
 * Filters out duplicate components using timestamp
 * and dispatches sendAgentReply if ForethoughtLiveChat document
 * transcript should be updated with message
 */
export const addLiveChatWidgetComponents = createAppAsyncThunk(
  'liveChat/addWidgetComponents',
  async (
    {
      components,
      helpdeskConversationId,
      sendAgentMessage = false,
      status,
    }: {
      components: WidgetComponent[];
      helpdeskConversationId?: string;
      sendAgentMessage?: boolean;
      status?: LiveChatStatus;
    },
    { dispatch, getState },
  ) => {
    const state = getState();
    const featureFlags = selectFeatureFlags(state);

    /**
     * Check is used by Kustomer One Chat because there can be message events received
     * that belong to the same user but a different conversation that we are not controlling.
     */
    if (
      helpdeskConversationId &&
      state.liveChat.chatId !== helpdeskConversationId
    ) {
      return;
    }

    const filteredComponents = filterWidgetComponents({
      components,
      state,
    });

    if (filteredComponents.length > 0) {
      dispatch(addWidgetComponents(filteredComponents));

      if (status) {
        dispatch(setLiveChatStatus(status));
      }

      if (sendAgentMessage) {
        filteredComponents.forEach(component => {
          if (isTextMessageComponent(component)) {
            dispatch(
              sendAgentReply({
                attachments: component.component_fields.attachments,
                message: component.component_fields.message,
              }),
            );
          } else if (
            isButtonsComponent(component) &&
            component.component_fields.prompt
          ) {
            dispatch(
              sendAgentReply({
                message: component.component_fields.prompt,
              }),
            );
          }
        });
      }

      if (featureFlags.includes('one_chat_open_widget_on_new_message')) {
        dispatch(openWidget());
      }
    }
  },
);

/**
 * Updates ForethoughtLiveChat document with helpdesk conversation id
 * that was created on the frontend. Currently only used by Kustomer.
 */
export const updateForethoughtLiveChatHelpdeskConversationId =
  createAppAsyncThunk(
    'liveChat/updateForethoughtLiveChatHelpdeskConversationId',
    async (
      helpdeskConversationId: string,
      { getState, rejectWithValue },
    ): Promise<UpdateForethoughtLiveChatHelpdeskConversationIdResponse> => {
      const state = getState();

      try {
        return await ApiService.request(
          'update-forethought-live-chat-helpdesk-conversation-id',
          {
            data: { helpdesk_conversation_id: helpdeskConversationId },
            urlValues: {
              conversationId: state.workflows.conversationId,
            },
          },
        );
      } catch (error) {
        return rejectWithCustomFetchError(error, rejectWithValue);
      }
    },
  );

/**
 * Deletes the existing ForethoughtLiveChat for a conversation
 * both from redux and the db
 *
 * used by Kustomer and Zopim one chats when availability checks
 * fail on the frontend.
 */
const deleteForethoughtLiveChat = createAppAsyncThunk(
  'liveChat/deleteForethoughtLiveChat',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();

    // remove live chat widget components from redux store
    dispatch(deleteForethoughtLiveChatWidgetComponents());

    // remove live chat component from mongo
    try {
      return await ApiService.request('delete-forethought-live-chat', {
        urlValues: {
          conversationId: state.workflows.conversationId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const addLiveChatEventMessage = createAppAsyncThunk(
  'liveChat/addEventMessage',
  async (
    {
      message,
      preventRepeatedMessages = false,
      status,
      timestamp,
    }: {
      message: string;
      preventRepeatedMessages?: boolean;
      status?: LiveChatStatus;
      timestamp?: number;
    },
    { dispatch, getState },
  ) => {
    const state = getState();
    const lastWidgetComponent = selectLastWidgetComponent(state);

    if (preventRepeatedMessages) {
      if (
        lastWidgetComponent &&
        isEventMessageComponent(lastWidgetComponent) &&
        lastWidgetComponent.component_fields.message === message
      ) {
        // do not add the same event message again
        return;
      }
    }

    dispatch(
      addLiveChatWidgetComponents({
        components: [
          {
            component_fields: {
              message,
            },
            component_type: 'event_message',
            timestamp,
          } satisfies EventMessageComponent,
        ],
        status,
      }),
    );
  },
);

const debounceEndAgentTyping = debounce((callback: () => void) => {
  callback();
}, 5000);

/**
 * Sets the agent typing status and debounces to set
 * isAgentTyping to false after 5 seconds no matter what
 */
export const handleAgentTyping = createAppAsyncThunk(
  'liveChat/handleAgentTyping',
  async ({ isTyping }: { isTyping: boolean }, { dispatch }) => {
    dispatch(setIsAgentTyping(isTyping));
    debounceEndAgentTyping(() => {
      if (isTyping) {
        dispatch(setIsAgentTyping(false));
      }
    });
    return Promise.resolve();
  },
);

const resolveConversation = createAppAsyncThunk(
  'liveChat/resolveConversation',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();

    try {
      return await ApiService.request('live-chat-resolve-conversation', {
        urlValues: {
          conversationId: state.workflows.conversationId,
          helpdeskConversationId: state.liveChat.chatId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const failLiveChat = createAppAsyncThunk(
  'liveChat/failLiveChat',
  async (
    { error, integration }: { error: string; integration: HandoffIntegrations },
    { dispatch, getState },
  ) => {
    const state = getState();
    const liveChatComponentExists = selectLiveChatWidgetComponentExists(state);

    // prevent duplicate salesforce one chat failure events / updates
    if (integration === 'salesforce' && !liveChatComponentExists) {
      // do not try and update conversation again
      dispatch(
        sendTrackingEvent({
          event_type: 'widget-live-chat-event',
          integration,
          ...{
            description: 'Duplicate live chat failure event suppressed',
            error,
          },
        }),
      );
      return;
    }

    dispatch(
      sendTrackingEvent({
        error: error,
        event_type: 'widget-customer-handoff',
        integration: integration,
        question: '',
        success: false,
      }),
    );
    dispatch(setLiveChatStatus('failure'));
    dispatch(resetLiveChatState());
    await dispatch(deleteForethoughtLiveChat());
    dispatch(
      updateIntentConversation({
        contextVariables: {
          [CHAT_HANDOFF_FAILURE_MESSAGE]: error,
          [CHAT_HANDOFF_SUCCESS]: false,
        },
      }),
    );
  },
);

/**
 * Changes live chat status to `ended` and shows CSAT if
 * org is configured to do so.
 */
export const concludeLiveChat = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const isCsatPromptable = selectIsCsatPromptableForEndLiveChat(state);
  const liveChatIntegration = selectLiveChatIntegration(state);

  dispatch(setLiveChatStatus('ended'));

  if (isCsatPromptable) {
    switch (liveChatIntegration) {
      // kustomer and sunco handle csat natively
      case 'kustomer':
      case 'sunco':
        break;
      default:
        dispatch(
          setCsatStatesByKey({
            isCsatVisible: true,
            triggeredFrom: 'end-live-chat',
          }),
        );
    }
  }
};

/**
 * Clears any local live chat data e.g. localStorage, listeners, etc...
 *
 * THIS ACTION WILL RUN ON EVERY TAB
 */
export const clearLiveChat = createAppAsyncThunk(
  'liveChat/clearLiveChat',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const liveChatIntegration = selectLiveChatIntegration(state);
    const { integrationId } = selectIntegrationConfig(state);
    const widgetInitMethod = selectWidgetInitMethod(state);
    const isCurrentTabPrimary = selectIsCurrentTabPrimary(state);

    switch (liveChatIntegration) {
      case 'sunco':
        // Smooch methods can be undefined:
        try {
          if (isCurrentTabPrimary) {
            dispatch(
              sendTrackingEvent({
                description:
                  'Sunco Live Chat cleared Smooch session -- clearLiveChat',
                event_type: 'widget-smooch-event',
                integration: 'sunco-live-chat',
                ...getSmoochLocalStorage(integrationId),
              }),
            );
          }
          clearSmoochSession(integrationId);
          Smooch.destroy();
        } catch {
          // Continue regardless of error
        }
        break;
      case 'salesforce':
        clearWebStorage();
        clearInMemoryData();
        break;
      case 'zendesk':
        /**
         * This is a workaround for the fact that the Zopim SDK keeps a reference to the chat session
         * in the window.zChat variable. By setting it to undefined, we ensure that the next chat session
         * starts fresh without any old messages.
         */
        try {
          if (widgetInitMethod === 'script') {
            postMessage({ event: 'forethoughtZopimClearSession' }, '*');
          } else {
            window.zChat = undefined;
          }
        } catch (e) {
          // Continue regardless of error
        }
        break;
      case 'kustomer':
        /**
         * useEffect cleanup function is enough here
         */
        break;
      case 'intercom':
      case 'freshchat':
        /**
         * New events are handled by our server and won't be subscribed to
         * as soon as the widget is cleared and a new conversation id is returned
         * by `/widget-config`
         */
        break;
    }
  },
);

/**
 * End a live chat. Then either stay in the widget or dispatch `clearConversation`.
 *
 * THIS ACTION WILL RUN ONLY ON THE PRIMARY TAB
 *
 * @param force - Force skips any stay in widget type logic. Used to immediately end the chat regardless of feature flags or user state.
 * @param shouldClearConversation - If true, clears the conversation from storage and  state. Used by `useClearConversationOnTimeout`.
 * @param shouldCloseWidget - If true, closes/minimizes the widget after ending the chat.  Used by `useClearConversationOnTimeout`.
 * @param shouldResolveConversation - If true, resolves the conversation in the backend.   Used for Freshchat integration.
 * @param shouldSendUserLeftMessage - If true, sends a "user left" message to the integration. Used for Sunco only.
 */
export const endLiveChat = createAppAsyncThunk<
  void,
  {
    force?: boolean;
    shouldClearConversation?: boolean;
    shouldResolveConversation?: boolean;
    shouldSendUserLeftMessage?: boolean;
  } | void
>(
  'liveChat/endLiveChat',
  async (
    {
      force = false,
      shouldClearConversation = true,
      shouldResolveConversation = true,
      shouldSendUserLeftMessage = true,
    } = {},
    { dispatch, getState },
  ) => {
    const state = getState();
    const liveChatIntegration = selectLiveChatIntegration(state);
    const chatId = selectChatId(state);
    const liveChatStatus = selectLiveChatStatus(state);
    const userEndedChat = selectUserEndedChat(state);
    const conversationId = selectConversationId(state);
    const widgetInitMethod = selectWidgetInitMethod(state);
    const featureFlags = selectFeatureFlags(state);
    const isStayAfterEndLiveChatEnabled = featureFlags.includes(
      'stay_after_end_live_chat',
    );

    /**
     * Helper function to clear conversation
     */
    const clearConvo = () => {
      if (shouldClearConversation) {
        dispatch(
          clearConversation({
            reason: 'end_live_chat',
            // dont want clearConversation to dispatch this action a second time
            shouldEndLiveChat: false,
          }),
        );
      }
    };

    /**
     * Helpdesk specific clear live chat logic and calls
     * to `clearConvo`
     */
    switch (liveChatIntegration) {
      case 'sunco':
        if (shouldSendUserLeftMessage) {
          // Smooch methods can be undefined:
          try {
            await Smooch.sendMessage(USER_LEFT_CHAT_MSG, chatId);
          } catch {
            // Continue regardless of error
          }
        }

        // If inside agent chat and ff is enabled. We should stay in the current page
        // instead of clear everything and start a new convo.
        if (
          isStayAfterEndLiveChatEnabled &&
          liveChatStatus === 'active' &&
          !userEndedChat &&
          !force
        ) {
          // Need timeout to allow userLeftMessage request to be sent
          setTimeout(() => {
            dispatch(setUserEndedChat(true));
            dispatch(concludeLiveChat());
            dispatch(
              addLiveChatEventMessage({
                message: 'Chat ended',
              }),
            );
          }, 400);

          return;
        } else {
          clearConvo();
        }
        break;
      case 'salesforce':
        closeConversation(chatId)
          .catch((err: unknown) => {
            sendErrorToSentry({
              additionalContext: { chatId },
              conversationId: conversationId,
              error: `Salesforce One Chat: Something went wrong in closing the conversation with conversation-id ${chatId}: ${getErrorMessage(err)}`,
            });
          })
          .finally(() => {
            closeEventSource().catch((err: unknown) => {
              sendErrorToSentry({
                additionalContext: { chatId },
                conversationId: conversationId,
                error: `Salesforce One Chat: Something went wrong in closing the Event Source (SSE): ${getErrorMessage(err)}`,
              });
            });

            clearConvo();
          });
        break;
      case 'zendesk':
        applyZopimFunction(widgetInitMethod, 'endChat', {
          clear_dept_id_on_chat_ended: true,
        });

        /**
         * when zendesk agent leaves it is set to ended and if it disconnects it is
         * set to inactive, sending another message would start a new zendesk chat
         * so don't do that.
         */
        if (liveChatStatus !== 'ended' && liveChatStatus !== 'inactive') {
          await dispatch(
            sendUserReply({
              addWidgetComponent: false,
              message: USER_LEFT_CHAT_MSG,
            }),
          );
        }

        clearConvo();
        break;
      case 'kustomer':
        try {
          await applyKustomerCoreFunctionWithCallback('endConversation', {
            conversationId: chatId,
          });
        } catch (err) {
          sendErrorToSentry({
            additionalContext: { chatId },
            conversationId: conversationId,
            error: `Failed to end Kustomer One Chat conversation -- ${err}`,
          });
          clearConvo();
          break;
        }

        if (
          userEndedChat ||
          liveChatStatus === 'inactive' ||
          liveChatStatus === 'ended' ||
          force
        ) {
          /**
           * conversation was already ended by the user once meaning
           * we called endConversation or we already received the
           * onConversationEnd event so we don't need to wait
           * for the onSatifactionReceived event.
           */
          clearConvo();
        } else {
          /**
           * conversation was ended in the above try block but we don't
           * want to close the widget to allow the onSatisfactionReceived
           * event to be triggered.
           */
          await dispatch(
            sendUserReply({
              addWidgetComponent: false,
              message: USER_LEFT_CHAT_MSG,
            }),
          );
          dispatch(setUserEndedChat(true));
        }
        break;
      case 'freshchat':
        if (shouldResolveConversation) {
          await dispatch(resolveConversation());
        }
        clearConvo();
        break;
      case 'intercom':
        await dispatch(sendUserReply({ message: USER_LEFT_CHAT_MSG }));
        clearConvo();
        break;
      default:
        clearConvo();
        break;
    }

    /**
     * Screen reader notification
     *
     * This can be called from useClearConversationOnTimeout but we only
     * want this additional logic if we are actually in a live chat.
     */
    if (liveChatIntegration) {
      dispatch(addScreenReaderNotification('Chat ended'));
    }
  },
);

export const handleLiveChatInput = createAppAsyncThunk(
  'liveChat/handleLiveChatInput',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const liveChatIntegration = selectLiveChatIntegration(state);
    const liveChatQuery = selectLiveChatQuery(state);
    const chatId = selectChatId(state);
    const { integrationId } = selectIntegrationConfig(state);
    const conversationId = selectConversationId(state);
    const widgetInitMethod = selectWidgetInitMethod(state);
    const isInAssistantMode = selectIsInAssistantMode(state);

    const handleSuncoLiveChat = async () => {
      // Disregard typing errors
      try {
        Smooch.stopTyping();
      } catch {
        // Continue regardless of error
      }

      /**
       * Send the message to smooch
       *
       * widget component is added in the Smooch message:sent handler
       */
      try {
        await Smooch.sendMessage(liveChatQuery, chatId);
        dispatch(setLiveChatQuery(''));
        dispatch(setDismissableNotification(''));
      } catch (error) {
        const errorMessage = 'Message failed to send';
        dispatch(addScreenReaderNotification(errorMessage));
        dispatch(setDismissableNotification(errorMessage));
        sendErrorToSentry({
          additionalContext: {
            ...getSmoochLocalStorage(integrationId),
          },
          conversationId: conversationId,
          error: `Sunco Live Chat failed to send message -- ${getSmoochErrorMessage(error)}`,
        });
      }
    };

    switch (liveChatIntegration) {
      case 'sunco':
        handleSuncoLiveChat();
        break;
      case 'salesforce':
        sendTextMessage(chatId, liveChatQuery, uuidv4())
          .then(() => {
            dispatch(sendUserReply({ message: liveChatQuery }));
            dispatch(setLiveChatQuery(''));
          })
          .catch((err: unknown) => {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: { chatId, message: liveChatQuery },
              conversationId: conversationId,
              error: `Salesforce One Chat failed to send message -- ${getErrorMessage(err)}`,
            });
          });
        break;
      case 'zendesk':
        applyZopimFunction(widgetInitMethod, 'sendChatMsg', liveChatQuery);
        dispatch(setLiveChatQuery(''));
        break;
      case 'kustomer':
        applyKustomerCoreFunctionWithCallback('sendMessage', {
          body: liveChatQuery,
          conversationId: chatId,
          isInAssistantMode,
        })
          .then(response => {
            dispatch(sendUserReply({ message: response.body || '' }));
            dispatch(setLiveChatQuery(''));
          })
          .catch((err: unknown) => {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: { chatId, message: liveChatQuery },
              conversationId: conversationId,
              error: `Kustomer One Chat failed to send message -- ${err}`,
            });
          });
        break;
      default:
        dispatch(sendUserReply({ message: liveChatQuery }));
        dispatch(setLiveChatQuery(''));
    }

    // TODO: let each integration handle this independently
    dispatch(addScreenReaderNotification('Message sent'));
  },
);

export const handleZopimChatId = createAppAsyncThunk(
  'liveChat/handleZopimChatId',
  async (newChatId: string, { dispatch, getState }) => {
    const state = getState();
    const currentChatId = selectChatId(state);

    if (newChatId && newChatId !== currentChatId) {
      // set in redux
      dispatch(setLiveChatId(newChatId));

      // update id on the forethought_live_chat mongo document
      await dispatch(
        updateForethoughtLiveChatHelpdeskConversationId(newChatId),
      );

      // tracking event
      dispatch(
        sendTrackingEvent({
          description: `Zopim chat session id set/updated to ${newChatId}`,
          event_type: 'widget-zopim-event',
          integration: 'zendesk-zopim-live-chat',
        }),
      );
    }
  },
);

export const mergeUnhandledSuncoMessages = createAppAsyncThunk(
  'liveChat/mergeUnhandledSuncoMessages',
  async (suncoMessages: SuncoMessage[], { dispatch, getState }) => {
    const state = getState();
    const lastWidgetComponent = selectLastWidgetComponent(state);

    /**
     * If multi-convo is enabled and user is logged in we will get all the messages
     * but we only want to render those after the latest transcript message.
     */
    const lastTranscriptMessage = suncoMessages.findLastIndex(
      message =>
        typeof message.text === 'string' &&
        message.text.includes(PREFIX_TO_SKIP_RENDERING_WIDGET_COMPONENT),
    );
    const filteredSuncoMessages =
      lastTranscriptMessage !== -1
        ? suncoMessages.slice(lastTranscriptMessage + 1)
        : suncoMessages;

    if (!lastWidgetComponent) {
      return;
    }

    const newWidgetComponents: WidgetComponent[] = [];

    // latest widget component is sunco live chat, that means the end user
    // is still in the pending state and hasn't received anything. Let's merge all messages
    if (lastWidgetComponent.component_type === 'sunco_live_chat') {
      filteredSuncoMessages.forEach(message => {
        const comp = processMessage(message);
        if (comp) {
          newWidgetComponents.push(comp);
        }
      });
      dispatch(
        addLiveChatWidgetComponents({
          components: newWidgetComponents,
          sendAgentMessage: true,
        }),
      );
      return;
    }

    // find the break point for new messages
    const latestTimestamp =
      'timestamp' in lastWidgetComponent ? lastWidgetComponent.timestamp : null;
    const matchingMsgIndex = filteredSuncoMessages.findIndex(
      message => message.received === latestTimestamp,
    );

    if (
      matchingMsgIndex === -1 ||
      matchingMsgIndex === filteredSuncoMessages.length - 1
    ) {
      return;
    }

    // all messages that have larger timestamps than the latest widget component
    // are new messages
    const newMessages = filteredSuncoMessages.slice(matchingMsgIndex + 1);
    newMessages.forEach(message => {
      const comp = processMessage(message);
      if (comp) {
        newWidgetComponents.push(comp);
      }
    });

    if (newWidgetComponents.length > 0) {
      /**
       * If adding an agent message, then dispatch onAgentConnected
       */
      const agentMessageIndex = newMessages.findLastIndex(
        message =>
          message.role !== 'user' &&
          message.source.type !== 'forethought' &&
          message.source.type !== 'web' &&
          message.displayName !== SUNCO_WAIT_TIME_NAME,
      );
      if (agentMessageIndex !== -1) {
        dispatch(
          onAgentConnected({
            agentIcon: newMessages[agentMessageIndex]?.avatarUrl,
            agentName: newMessages[agentMessageIndex]?.displayName,
          }),
        );
      }

      dispatch(
        addLiveChatWidgetComponents({
          components: newWidgetComponents,
          sendAgentMessage: true,
        }),
      );
    }
  },
);
